import React, { useMemo, useState } from "react";

/* global pdfjsLib */

const ROWS_PER_PAGE = 50;
const CORRECT_PASSWORD = "Zahra02152023@#@#!!"; // Change this to your desired password

const App = () => {
  // Authentication state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  // Dashboard states
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [debugInfo, setDebugInfo] = useState({
    rawText: "",
    parsedLines: 0,
    successfulParses: 0,
    failedParses: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    minAmount: "",
    maxAmount: "",
    city: "",
    sortBy: "",
    sortDirection: "asc",
  });

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      setIsLoggedIn(true);
      setLoginError("");
    } else {
      setLoginError("Incorrect password");
    }
  };

  // PDF Parsing Functions
  const parsePdfData = (text) => {
    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    setDebugInfo((prev) => ({ ...prev, parsedLines: lines.length }));

    let data = [];
    let currentEntry = {};
    let isHeaderPassed = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.includes("OWNER NAME")) {
        isHeaderPassed = true;
        continue;
      }

      if (!isHeaderPassed) continue;

      if (
        line.includes("Hamilton County") ||
        line.includes("Jill Schiller") ||
        line.includes("For More Info") ||
        line.includes("Page:") ||
        line.match(/^\d{2}\/\d{2}\/\d{4}$/)
      ) {
        continue;
      }

      try {
        if (line.includes("$")) {
          const amount = parseFloat(line.replace(/[$,]/g, ""));
          currentEntry = {
            ...currentEntry,
            amount: amount,
          };
          if (currentEntry.owner) {
            data.push(currentEntry);
          }
          currentEntry = {};
        } else if (line === "OH") {
          currentEntry.state = line;
        } else if (line.match(/^\d{5}$/)) {
          currentEntry.zip = line;
        } else if (
          line === "CINCINNATI" ||
          line === "BLUE ASH" ||
          line === "HARRISON" ||
          line === "SPRINGFIELD TWNHP"
        ) {
          currentEntry.city = line;
        } else if (
          line.match(/^\d/) ||
          line.includes("PO BOX") ||
          line.includes("APT") ||
          line.includes("UNKNOWN")
        ) {
          currentEntry.address = line;
        } else {
          currentEntry.owner = line;
        }
      } catch (e) {
        console.error("Error parsing line:", line, e);
      }
    }

    const successfulParses = data.length;
    setDebugInfo((prev) => ({
      ...prev,
      successfulParses,
      failedParses: prev.parsedLines - successfulParses,
    }));

    return data;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setAllData([]);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let allParsedData = [];

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item, index) => {
            const nextItem = textContent.items[index + 1];
            if (
              nextItem &&
              Math.abs(item.transform[5] - nextItem.transform[5]) > 5
            ) {
              return item.str + "\n";
            }
            return item.str + " ";
          })
          .join("");

        const parsedData = parsePdfData(pageText);
        allParsedData = [...allParsedData, ...parsedData];
      }

      setAllData(allParsedData);
    } catch (err) {
      console.error("PDF processing error:", err);
      setError("Error processing PDF file: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search criteria
  const filteredData = useMemo(() => {
    return allData.filter((item) => {
      const matchesSearch =
        !filters.search ||
        Object.values(item).some((val) =>
          val.toString().toLowerCase().includes(filters.search.toLowerCase())
        );

      const matchesMinAmount =
        !filters.minAmount || item.amount >= parseFloat(filters.minAmount);

      const matchesMaxAmount =
        !filters.maxAmount || item.amount <= parseFloat(filters.maxAmount);

      const matchesCity =
        !filters.city ||
        item.city.toLowerCase().includes(filters.city.toLowerCase());

      return (
        matchesSearch && matchesMinAmount && matchesMaxAmount && matchesCity
      );
    });
  }, [allData, filters]);

  // Get paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
    return filteredData.slice(startIndex, startIndex + ROWS_PER_PAGE);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / ROWS_PER_PAGE);

  // Login screen if not authenticated
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
            Hamilton County Dashboard Login
          </h2>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter password"
              />
            </div>
            {loginError && (
              <div className="text-red-500 text-sm">{loginError}</div>
            )}
            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Dashboard view if authenticated
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-800">
              Hamilton County - Unclaimed Funds Dashboard
            </h1>
            <button
              onClick={() => setIsLoggedIn(false)}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Logout
            </button>
          </div>
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4
              file:rounded-md file:border-0 file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {loading && <div className="mt-2 text-gray-600">Processing...</div>}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Processing Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded p-4">
              <div className="text-sm text-gray-600">Parsed Lines</div>
              <div className="text-2xl font-bold text-blue-600">
                {debugInfo.parsedLines}
              </div>
            </div>
            <div className="bg-green-50 rounded p-4">
              <div className="text-sm text-gray-600">Successful Parses</div>
              <div className="text-2xl font-bold text-green-600">
                {debugInfo.successfulParses}
              </div>
            </div>
            <div className="bg-red-50 rounded p-4">
              <div className="text-sm text-gray-600">Failed Parses</div>
              <div className="text-2xl font-bold text-red-600">
                {debugInfo.failedParses}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                placeholder="Search records..."
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Amount
              </label>
              <input
                type="number"
                value={filters.minAmount}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, minAmount: e.target.value }))
                }
                placeholder="Min amount..."
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Amount
              </label>
              <input
                type="number"
                value={filters.maxAmount}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, maxAmount: e.target.value }))
                }
                placeholder="Max amount..."
                className="w-full p-2 border rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                value={filters.city}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, city: e.target.value }))
                }
                placeholder="Filter by city..."
                className="w-full p-2 border rounded"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Owner
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    State
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ZIP
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.owner}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.address}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.city}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.state}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.zip}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      $
                      {item.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {paginatedData.length} of {filteredData.length} results
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border rounded text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
